import { ProjectPlane } from "@faro-lotv/lotv";
import { Box3, Matrix4, Plane, Vector2, Vector3 } from "three";
import { depthPosition, surfaceGeometry } from "./depth-surface";
import type { SurfaceSelection } from "./surface-selection-pass";
import { CylinderDeviationSamples } from "./cylinder-deviation-histogram";
import {
  cylinderDistance,
  cylinderGeometry,
  cylinderPosition,
} from "./cylinder-surface";

const CELL_SIZE = 0.05;
const GRID_SIZE = 1024;
const MIN_ALIGNMENT = Math.cos(Math.PI / 12);
const BATCH_SIZE = 8192;
const NEIGHBOR_RADIUS = 2;
export const SURFACE_TILE_SIZE = 32;

export type SurfaceCoverage = "growing" | "complete" | "partial";

/** Camera-independent connected plane mask or axially connected cylinder mask. */
export class WorldSurface {
  readonly basis: ProjectPlane;
  readonly mask: Uint8Array;
  readonly samples: Float32Array;
  readonly width: number;
  readonly sampleSpacing: number;
  readonly seedTiles = new Set<number>();
  readonly plane: Plane;
  readonly cellSize = CELL_SIZE;
  readonly size = GRID_SIZE;
  private readonly uv = new Vector2();
  private readonly point = new Vector3();
  private readonly offset = new Vector3();
  private readonly tangent = new Vector3();
  private readonly capturedPoint = new Vector3();
  private readonly minimum = new Vector2(GRID_SIZE, GRID_SIZE);
  private readonly maximum = new Vector2(-1, -1);
  private readonly propagated = new Set<string>();
  private readonly selectedRows = new Uint8Array(GRID_SIZE);
  private readonly cylinderSamples: CylinderDeviationSamples | undefined;
  limited = false;

  /**
   * @param seed The immediate captured selection.
   */
  constructor(readonly seed: SurfaceSelection) {
    this.width = seed.cylinder
      ? Math.min(
          GRID_SIZE,
          Math.max(
            SURFACE_TILE_SIZE,
            Math.ceil(
              (Math.PI * 2 * seed.cylinder.radius) /
                CELL_SIZE /
                SURFACE_TILE_SIZE,
            ) * SURFACE_TILE_SIZE,
          ),
        )
      : GRID_SIZE;
    this.mask = new Uint8Array(this.width * GRID_SIZE);
    this.sampleSpacing = seed.cylinder
      ? Math.min(CELL_SIZE, (Math.PI * 2 * seed.cylinder.radius) / this.width)
      : CELL_SIZE;
    this.samples = new Float32Array(this.mask.length).fill(Infinity);
    if (seed.cylinder) {
      this.cylinderSamples = new CylinderDeviationSamples();
      this.tangent.crossVectors(seed.cylinder.axis, seed.cylinder.radial);
    }
    this.plane = new Plane(
      new Vector3(seed.plane.x, seed.plane.y, seed.plane.z),
      seed.plane.w,
    );
    this.basis = new ProjectPlane(
      this.plane.projectPoint(seed.origin, new Vector3()),
      this.plane.normal.clone(),
    );
  }

  /**
   * @param column Grid column.
   * @param row Grid row.
   * @returns Linear cell index.
   */
  index(column: number, row: number): number {
    return row * this.width + column;
  }

  /**
   * @param column Grid column.
   * @param row Grid row.
   * @returns Tile containing the cell.
   */
  tile(column: number, row: number): number {
    return (
      Math.floor(row / SURFACE_TILE_SIZE) * (this.width / SURFACE_TILE_SIZE) +
      Math.floor(column / SURFACE_TILE_SIZE)
    );
  }

  /**
   * @param point World-space sample.
   * @returns Reusable grid coordinates.
   */
  coordinates(point: Vector3): Vector2 {
    const { cylinder } = this.seed;
    if (cylinder) {
      this.offset.subVectors(point, cylinder.center);
      const angle = Math.atan2(
        this.offset.dot(this.tangent),
        this.offset.dot(cylinder.radial),
      );
      return this.uv.set(
        Math.floor((angle / (Math.PI * 2) + 0.5) * this.width) % this.width,
        Math.floor(this.offset.dot(cylinder.axis) / CELL_SIZE + GRID_SIZE / 2),
      );
    }
    this.basis.project(point, this.uv);
    return this.uv
      .divideScalar(CELL_SIZE)
      .addScalar(GRID_SIZE / 2)
      .floor();
  }

  private select(column: number, row: number): void {
    this.mask[this.index(column, row)] = 255;
    this.selectedRows[row] = 1;
    this.minimum.min(new Vector2(column, row));
    this.maximum.max(new Vector2(column, row));
    if (
      (!this.seed.cylinder && (column === 0 || column === this.width - 1)) ||
      row === 0 ||
      row === GRID_SIZE - 1
    ) {
      this.limited = true;
    }
  }

  /**
   * @param cancelled Whether selection work should stop.
   */
  async initialize(cancelled: () => boolean): Promise<void> {
    await this.addSelection(this.seed, cancelled);
  }

  /**
   * @param points Observed near-seed support for a corrected cylinder, in world coordinates.
   */
  initializeCylinderPoints(points: readonly Vector3[]): void {
    const { cylinder, tolerance } = this.seed;
    if (!cylinder) return;
    for (const point of points) {
      if (
        !point.toArray().every(Number.isFinite) ||
        Math.abs(cylinderDistance(cylinder, point)) > tolerance
      ) {
        continue;
      }
      const cell = this.coordinates(point);
      if (
        cell.x < 0 ||
        cell.x >= this.width ||
        cell.y < 0 ||
        cell.y >= GRID_SIZE
      ) {
        continue;
      }
      this.select(cell.x, cell.y);
      this.seedTiles.add(this.tile(cell.x, cell.y));
      this.addPoint(point);
    }
  }

  /**
   * @param seed Another captured or world-grid patch on the same fixed plane.
   * @param cancelled Whether selection work should stop.
   */
  async addSelection(
    seed: SurfaceSelection,
    cancelled: () => boolean,
  ): Promise<void> {
    const lower = new Vector2(GRID_SIZE, GRID_SIZE);
    const upper = new Vector2(-1, -1);
    if (seed.cylinderSamples) {
      for (let index = 0; index < seed.cylinderSamples.length; index += 3) {
        this.cylinderSamples?.add(
          this.point.fromArray(seed.cylinderSamples, index),
        );
      }
    }
    for (let index = 0; index < seed.mask.length; index++) {
      if (!seed.mask[index]) continue;
      if (seed.worldSpace) {
        if (seed.cylinder) {
          cylinderPosition(
            seed,
            index % seed.width,
            Math.floor(index / seed.width),
            this.point,
          );
        } else {
          this.point
            .set(
              (((index % seed.width) + 0.5) / seed.width) * 2 - 1,
              ((Math.floor(index / seed.width) + 0.5) / seed.height) * 2 - 1,
              0,
            )
            .applyMatrix4(seed.inverseViewProjection);
        }
      } else {
        depthPosition(
          seed,
          index % seed.width,
          Math.floor(index / seed.width),
          this.point,
        );
      }
      const cell = this.coordinates(this.point);
      lower.min(cell);
      upper.max(cell);
      if (
        this.seed.cylinder &&
        !seed.worldSpace &&
        cell.x >= 0 &&
        cell.x < this.width &&
        cell.y >= 0 &&
        cell.y < GRID_SIZE &&
        Math.abs(cylinderDistance(this.seed.cylinder, this.point)) <=
          this.seed.tolerance
      ) {
        this.select(cell.x, cell.y);
        this.seedTiles.add(this.tile(cell.x, cell.y));
        if (!seed.cylinderSamples) this.cylinderSamples?.add(this.point);
      }
    }
    this.limited =
      this.limited ||
      lower.x < 0 ||
      lower.y < 0 ||
      upper.x >= this.width ||
      upper.y >= GRID_SIZE;
    lower.clampScalar(0, GRID_SIZE - 1);
    upper.clampScalar(0, GRID_SIZE - 1);
    upper.x = Math.min(upper.x, this.width - 1);
    let processed = 0;
    for (let row = lower.y; row <= upper.y; row++) {
      for (let column = lower.x; column <= upper.x; column++) {
        if (cancelled()) return;
        this.worldPoint(column, row, this.point);
        this.capturedPoint.copy(this.point);
        this.point.applyMatrix4(seed.viewProjection);
        if (seed.cylinder && seed.worldSpace) {
          this.point.set(
            Math.atan2(this.point.y, this.point.x) / Math.PI,
            this.point.z,
            0,
          );
        }
        const capturedColumn = Math.floor(
          ((this.point.x + 1) * seed.width) / 2,
        );
        const capturedRow = Math.floor(((this.point.y + 1) * seed.height) / 2);
        if (
          capturedColumn >= 0 &&
          capturedColumn < seed.width &&
          capturedRow >= 0 &&
          capturedRow < seed.height &&
          seed.mask[capturedRow * seed.width + capturedColumn] &&
          (!seed.cylinder ||
            seed.worldSpace ||
            this.matchesCapturedCylinder(seed, capturedColumn, capturedRow))
        ) {
          this.select(column, row);
          this.seedTiles.add(this.tile(column, row));
        }
        if (++processed % BATCH_SIZE === 0) {
          await new Promise<void>((resolve) => setTimeout(resolve, 0));
        }
      }
    }
  }

  private matchesCapturedCylinder(
    seed: SurfaceSelection,
    column: number,
    row: number,
  ): boolean {
    if (!seed.cylinder) return false;
    depthPosition(seed, column, row, this.offset).sub(this.capturedPoint);
    const axial = this.offset.dot(seed.cylinder.axis);
    const radialSquared = Math.max(0, this.offset.lengthSq() - axial ** 2);
    return (
      this.offset.length() <= Math.max(seed.tolerance * 3, CELL_SIZE * 2) &&
      radialSquared <= (seed.cylinder.radius / 2) ** 2
    );
  }

  /**
   * @param column Grid column.
   * @param row Grid row.
   * @param target Reusable output point.
   * @returns World-space cell center on the plane.
   */
  worldPoint(column: number, row: number, target: Vector3): Vector3 {
    const { cylinder } = this.seed;
    if (cylinder) {
      const angle = ((column + 0.5) / this.width - 0.5) * Math.PI * 2;
      return target
        .copy(cylinder.center)
        .addScaledVector(cylinder.radial, Math.cos(angle) * cylinder.radius)
        .addScaledVector(this.tangent, Math.sin(angle) * cylinder.radius)
        .addScaledVector(
          cylinder.axis,
          (row + 0.5 - GRID_SIZE / 2) * CELL_SIZE,
        );
    }
    return this.basis.unProject(
      new Vector2(
        (column + 0.5 - GRID_SIZE / 2) * CELL_SIZE,
        (row + 0.5 - GRID_SIZE / 2) * CELL_SIZE,
      ),
      target,
    );
  }

  /**
   * @param point World-space source sample, including off-plane neighbors.
   */
  addPoint(point: Vector3): void {
    const cell = this.coordinates(point);
    if (
      cell.x < 0 ||
      cell.y < 0 ||
      cell.x >= this.width ||
      cell.y >= GRID_SIZE
    ) {
      return;
    }
    const distance = this.seed.cylinder
      ? cylinderDistance(this.seed.cylinder, point)
      : this.plane.distanceToPoint(point);
    if (Math.abs(distance) <= this.seed.tolerance) {
      this.cylinderSamples?.add(point);
    }
    const index = this.index(cell.x, cell.y);
    if (Math.abs(distance) < Math.abs(this.samples[index])) {
      this.samples[index] = distance;
    }
  }

  /**
   * @param tile Tile index.
   * @returns Grid origin and conservative world bounds including normal support.
   */
  tileBounds(tile: number): { column: number; row: number; box: Box3 } {
    const column =
      (tile % (this.width / SURFACE_TILE_SIZE)) * SURFACE_TILE_SIZE;
    const row =
      Math.floor(tile / (this.width / SURFACE_TILE_SIZE)) * SURFACE_TILE_SIZE;
    const box = new Box3();
    if (this.seed.cylinder) {
      for (
        let offset = -NEIGHBOR_RADIUS;
        offset <= SURFACE_TILE_SIZE + NEIGHBOR_RADIUS;
        offset++
      ) {
        for (const edge of [
          -NEIGHBOR_RADIUS,
          SURFACE_TILE_SIZE + NEIGHBOR_RADIUS,
        ]) {
          box.expandByPoint(
            this.worldPoint(column + offset, row + edge, this.point),
          );
        }
      }
      return { column, row, box: box.expandByScalar(CELL_SIZE * 4) };
    }
    for (const offsetColumn of [
      -NEIGHBOR_RADIUS,
      SURFACE_TILE_SIZE + NEIGHBOR_RADIUS,
    ]) {
      for (const offsetRow of [
        -NEIGHBOR_RADIUS,
        SURFACE_TILE_SIZE + NEIGHBOR_RADIUS,
      ]) {
        this.worldPoint(column + offsetColumn, row + offsetRow, this.point);
        box.expandByPoint(
          this.point.clone().addScaledVector(this.plane.normal, CELL_SIZE * 4),
        );
        box.expandByPoint(
          this.point.clone().addScaledVector(this.plane.normal, -CELL_SIZE * 4),
        );
      }
    }
    return { column, row, box };
  }

  private slope(
    column: number,
    row: number,
    horizontal: boolean,
  ): number | undefined {
    const center = this.samples[this.index(column, row)];
    let best: number | undefined;
    for (const step of [-NEIGHBOR_RADIUS, -1, 1, NEIGHBOR_RADIUS]) {
      const neighborColumn = this.wrapColumn(column + (horizontal ? step : 0));
      const neighborRow = row + (horizontal ? 0 : step);
      if (
        neighborColumn < 0 ||
        neighborRow < 0 ||
        neighborColumn >= this.width ||
        neighborRow >= GRID_SIZE
      ) {
        continue;
      }
      const neighbor = this.samples[this.index(neighborColumn, neighborRow)];
      const spacing =
        horizontal && this.seed.cylinder
          ? (Math.PI * 2 * this.seed.cylinder.radius) / this.width
          : CELL_SIZE;
      const slope = (neighbor - center) / (step * spacing);
      if (
        Number.isFinite(slope) &&
        (best === undefined || Math.abs(slope) < Math.abs(best))
      ) {
        best = slope;
      }
    }
    return best;
  }

  /**
   * @param tile Tile index.
   * @returns Whether every tile cell is already part of the seed.
   */
  covered(tile: number): boolean {
    const { column, row } = this.tileBounds(tile);
    for (let offset = 0; offset < SURFACE_TILE_SIZE; offset++) {
      const start = this.index(column, row + offset);
      if (
        this.mask
          .subarray(start, start + SURFACE_TILE_SIZE)
          .some((value) => !value)
      ) {
        return false;
      }
    }
    return true;
  }

  private wrapColumn(column: number): number {
    return this.seed.cylinder ? (column + this.width) % this.width : column;
  }

  /**
   * @param tile Tile index.
   * @returns Tiles reached through selected neighbors or selected cylinder rows.
   */
  grow(tile: number): Set<number> {
    const { column: startColumn, row: startRow } = this.tileBounds(tile);
    const queue: number[] = [];
    const queued = new Set<number>();
    const frontier = new Set<number>();
    const expandedRows = new Set<number>();
    const visit = (column: number, row: number): void => {
      column = this.wrapColumn(column);
      if (column < 0 || row < 0 || column >= this.width || row >= GRID_SIZE) {
        return;
      }
      const index = this.index(column, row);
      if (queued.has(index)) return;
      if (this.tile(column, row) !== tile) {
        const edge = `${tile}:${index}`;
        if (!this.propagated.has(edge)) {
          this.propagated.add(edge);
          frontier.add(this.tile(column, row));
        }
        return;
      }
      queued.add(index);
      if (!this.mask[index]) {
        if (Math.abs(this.samples[index]) > this.seed.tolerance) return;
        if (!this.seed.cylinder) {
          const horizontal = this.slope(column, row, true);
          const vertical = this.slope(column, row, false);
          if (
            horizontal === undefined ||
            vertical === undefined ||
            1 / Math.sqrt(1 + horizontal ** 2 + vertical ** 2) < MIN_ALIGNMENT
          ) {
            return;
          }
        }
        this.select(column, row);
      }
      queue.push(index);
    };
    for (let row = startRow; row < startRow + SURFACE_TILE_SIZE; row++) {
      for (
        let column = startColumn;
        column < startColumn + SURFACE_TILE_SIZE;
        column++
      ) {
        const index = this.index(column, row);
        if (
          this.mask[index] ||
          (this.seed.cylinder && this.selectedRows[row]) ||
          ((column > 0 || this.seed.cylinder) &&
            this.mask[this.index(this.wrapColumn(column - 1), row)]) ||
          ((column + 1 < this.width || this.seed.cylinder) &&
            this.mask[this.index(this.wrapColumn(column + 1), row)]) ||
          (row > 0 && this.mask[index - this.width]) ||
          (row + 1 < GRID_SIZE && this.mask[index + this.width])
        ) {
          visit(column, row);
        }
      }
    }
    for (const index of queue) {
      const column = index % this.width;
      const row = Math.floor(index / this.width);
      if (this.seed.cylinder && !expandedRows.has(row)) {
        expandedRows.add(row);
        for (let offset = 0; offset < SURFACE_TILE_SIZE; offset++) {
          visit(startColumn + offset, row);
        }
        for (let column = 0; column < this.width; column += SURFACE_TILE_SIZE) {
          const nextTile = this.tile(column, row);
          const edge = `ring:${row}:${nextTile}`;
          if (nextTile !== tile && !this.propagated.has(edge)) {
            this.propagated.add(edge);
            frontier.add(nextTile);
          }
        }
      }
      visit(column - 1, row);
      visit(column + 1, row);
      visit(column, row - 1);
      visit(column, row + 1);
    }
    return frontier;
  }

  /**
   * @param coverage Loading completion status.
   * @returns Cropped world mask and matching display geometry.
   */
  selection(coverage: SurfaceCoverage): SurfaceSelection {
    const { cylinder } = this.seed;
    const firstColumn = cylinder ? 0 : this.minimum.x;
    const width = cylinder
      ? this.width
      : Math.max(1, this.maximum.x - this.minimum.x + 1);
    const height = Math.max(1, this.maximum.y - this.minimum.y + 1);
    const mask = new Uint8Array(width * height);
    for (let row = 0; row < height; row++) {
      const start = this.index(firstColumn, this.minimum.y + row);
      mask.set(this.mask.subarray(start, start + width), row * width);
    }
    const center = this.worldPoint(
      this.minimum.x + (width - 1) / 2,
      this.minimum.y + (height - 1) / 2,
      new Vector3(),
    );
    const inverseViewProjection = new Matrix4()
      .makeBasis(this.basis.planeU, this.basis.planeV, this.plane.normal)
      .scale(new Vector3((width * CELL_SIZE) / 2, (height * CELL_SIZE) / 2, 1))
      .setPosition(center);
    if (cylinder) {
      center
        .copy(cylinder.center)
        .addScaledVector(
          cylinder.axis,
          (this.minimum.y + height / 2 - GRID_SIZE / 2) * CELL_SIZE,
        );
      inverseViewProjection
        .makeBasis(cylinder.radial, this.tangent, cylinder.axis)
        .scale(
          new Vector3(
            cylinder.radius,
            cylinder.radius,
            (height * CELL_SIZE) / 2,
          ),
        )
        .setPosition(center);
    }
    const snapshot = {
      width,
      height,
      inverseViewProjection,
      depth: new Float32Array(0),
    };
    return {
      ...this.seed,
      ...snapshot,
      mask,
      viewProjection: inverseViewProjection.clone().invert(),
      ...(cylinder
        ? cylinderGeometry(snapshot, mask, this.seed.origin)
        : surfaceGeometry(snapshot, mask, this.plane, this.seed.origin)),
      cylinderSamples: this.cylinderSamples?.snapshot((point) => {
        const cell = this.coordinates(point);
        return Boolean(this.mask[this.index(cell.x, cell.y)]);
      }),
      worldSpace: true,
      coverage,
    };
  }
}
